"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

export type VoiceGuideTrack = "onboarding" | "payments" | "features" | "outro";

interface VoiceGuideTrackMeta {
  label: string;
  description: string;
  src: string;
}

const TRACKS: Record<VoiceGuideTrack, VoiceGuideTrackMeta> = {
  onboarding: {
    label: "Welcome to BlockyNetworks",
    description: "A quick intro to getting started",
    src: "/audio/voice_guide/onboarding.aiff",
  },
  payments: {
    label: "Subscription & Billing",
    description: "How plans and payments work",
    src: "/audio/voice_guide/payments.aiff",
  },
  features: {
    label: "Features Overview",
    description: "Everything you can do on BlockyNetworks",
    src: "/audio/voice_guide/features.aiff",
  },
  outro: {
    label: "You're All Set",
    description: "Final tips before you dive in",
    src: "/audio/voice_guide/outro.aiff",
  },
};

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

interface VoiceGuidePlayerProps {
  track: VoiceGuideTrack;
  onEnded?: () => void;
  className?: string;
  autoPlay?: boolean;
}

export function VoiceGuidePlayer({
  track,
  onEnded,
  className,
  autoPlay = false,
}: VoiceGuidePlayerProps) {
  const meta = TRACKS[track];
  const audioRef = useRef<HTMLAudioElement>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const progressRef = useRef<HTMLDivElement>(null);

  // Sync play state
  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || hasError) return;
    if (isPlaying) {
      audio.pause();
    } else {
      void audio.play();
    }
  }, [isPlaying, hasError]);

  // Seek via progress bar click or keyboard
  const seek = useCallback((ratio: number) => {
    const audio = audioRef.current;
    if (!audio || !isFinite(audio.duration)) return;
    audio.currentTime = ratio * audio.duration;
  }, []);

  const handleProgressClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const bar = progressRef.current;
      if (!bar) return;
      const rect = bar.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      seek(ratio);
    },
    [seek]
  );

  // Keyboard support on the progress bar
  const handleProgressKey = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const audio = audioRef.current;
      if (!audio || !isFinite(audio.duration)) return;
      const step = audio.duration * 0.05; // 5% per keypress
      if (e.key === "ArrowRight" || e.key === "ArrowUp") {
        e.preventDefault();
        audio.currentTime = Math.min(audio.duration, audio.currentTime + step);
      } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
        e.preventDefault();
        audio.currentTime = Math.max(0, audio.currentTime - step);
      } else if (e.key === "Home") {
        e.preventDefault();
        audio.currentTime = 0;
      } else if (e.key === "End") {
        e.preventDefault();
        audio.currentTime = audio.duration;
      }
    },
    []
  );

  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = !audio.muted;
    setIsMuted(audio.muted);
  }, []);

  // Global spacebar shortcut for play/pause when focused inside player
  const handlePlayerKey = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === " " || e.key === "Enter") {
        const target = e.target as HTMLElement;
        // Only intercept space if focused on the player container itself, not a child button
        if (target.dataset.playertoggle) {
          e.preventDefault();
          togglePlay();
        }
      }
    },
    [togglePlay]
  );

  // Wire audio element events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onDurationChange = () => {
      if (isFinite(audio.duration)) setDuration(audio.duration);
    };
    const onCanPlay = () => setIsLoading(false);
    const onWaiting = () => setIsLoading(true);
    const onError = () => {
      setHasError(true);
      setIsLoading(false);
      setIsPlaying(false);
    };
    const onEnded_ = () => {
      setIsPlaying(false);
      onEnded?.();
    };

    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("durationchange", onDurationChange);
    audio.addEventListener("canplay", onCanPlay);
    audio.addEventListener("waiting", onWaiting);
    audio.addEventListener("error", onError);
    audio.addEventListener("ended", onEnded_);

    return () => {
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("durationchange", onDurationChange);
      audio.removeEventListener("canplay", onCanPlay);
      audio.removeEventListener("waiting", onWaiting);
      audio.removeEventListener("error", onError);
      audio.removeEventListener("ended", onEnded_);
    };
  }, [onEnded]);

  useEffect(() => {
    if (autoPlay && audioRef.current) {
      void audioRef.current.play().catch(() => {
        // Autoplay blocked — user gesture required, no-op
      });
    }
  }, [autoPlay]);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      className={cn(
        "rounded-xl border border-border bg-background p-4 shadow-sm",
        className
      )}
      role="region"
      aria-label={`Voice guide: ${meta.label}`}
      onKeyDown={handlePlayerKey}
    >
      {/* Hidden native audio element */}
      <audio ref={audioRef} preload="metadata" aria-hidden="true">
        <source src={meta.src} type="audio/aiff" />
        {/* Fallback message if audio is unsupported */}
      </audio>

      {/* Track info */}
      <div className="mb-3 flex items-start gap-3">
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
          aria-hidden="true"
        >
          <VoiceIcon />
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-foreground">{meta.label}</p>
          <p className="text-xs text-muted">{meta.description}</p>
        </div>
      </div>

      {hasError ? (
        <div className="rounded-md border border-border bg-foreground/5 px-3 py-2 text-xs text-muted">
          Audio unavailable in this browser. Please try Safari or update your browser.
        </div>
      ) : (
        <>
          {/* Progress bar */}
          <div
            ref={progressRef}
            role="slider"
            aria-label="Playback position"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress)}
            aria-valuetext={`${formatTime(currentTime)} of ${formatTime(duration)}`}
            tabIndex={0}
            className="group relative mb-3 h-2 cursor-pointer rounded-full bg-foreground/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            onClick={handleProgressClick}
            onKeyDown={handleProgressKey}
          >
            <div
              className="h-full rounded-full bg-primary transition-all duration-100"
              style={{ width: `${progress}%` }}
              aria-hidden="true"
            />
            {/* Thumb indicator */}
            <div
              className="absolute top-1/2 h-3.5 w-3.5 -translate-y-1/2 rounded-full border-2 border-primary bg-background shadow-sm transition-opacity group-hover:opacity-100"
              style={{ left: `calc(${progress}% - 7px)` }}
              aria-hidden="true"
            />
          </div>

          {/* Controls row */}
          <div className="flex items-center gap-3">
            {/* Play / Pause */}
            <button
              onClick={togglePlay}
              disabled={isLoading}
              aria-label={isPlaying ? "Pause" : "Play"}
              data-playertoggle="true"
              className={cn(
                "flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-50",
                isLoading && "animate-pulse"
              )}
            >
              {isLoading ? (
                <LoadingIcon className="h-4 w-4 animate-spin" />
              ) : isPlaying ? (
                <PauseIcon className="h-4 w-4" />
              ) : (
                <PlayIcon className="h-4 w-4" />
              )}
            </button>

            {/* Time */}
            <div
              className="min-w-[5rem] text-xs tabular-nums text-muted"
              aria-live="off"
              aria-atomic="true"
            >
              <span aria-label="current time">{formatTime(currentTime)}</span>
              <span aria-hidden="true"> / </span>
              <span aria-label="total duration">{formatTime(duration)}</span>
            </div>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Mute */}
            <button
              onClick={toggleMute}
              aria-label={isMuted ? "Unmute" : "Mute"}
              aria-pressed={isMuted}
              className="flex h-8 w-8 items-center justify-center rounded-md text-muted transition-colors hover:bg-foreground/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              {isMuted ? <MutedIcon className="h-4 w-4" /> : <SoundIcon className="h-4 w-4" />}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Icons ──────────────────────────────────────────────────────────────────

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M8 5.14v14l11-7-11-7z" />
    </svg>
  );
}

function PauseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
    </svg>
  );
}

function LoadingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  );
}

function VoiceIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4M12 3a4 4 0 014 4v4a4 4 0 01-8 0V7a4 4 0 014-4z" />
    </svg>
  );
}

function SoundIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072M12 6l-4 4H4v4h4l4 4V6z" />
    </svg>
  );
}

function MutedIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15zM17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
    </svg>
  );
}
